import React from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  arrangeData,
  convertDateToDb,
} from "@/middlewares/api";
import { normalizeMoney, cleanMoney } from "@/utils/forms.js";

import SelectField from "@/components/forms/select-field";
import FormField from "@/components/forms/form-field";
import RadioField from "@/components/forms/radio-field";
import DatePickerField from "@/components/forms/date-picker";
import WordsLimitedField from "@/components/forms/words-limited-field";
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';

const validationSchema = Yup.object().shape({
  territory: Yup.string().required('Required').max(255),
  release_air_date: Yup.string().required('Required'),
  option: Yup.string().required('Required').max(255),
  proposed_free: Yup.string() // Use string for formatted input
    .required('Required'),
  publishing_or_master_sync_used: Yup.string()
    .required('Selection is required')
    .oneOf(['master', 'publishing', 'other'], 'Invalid selection'),
  sync_other_used: Yup.string().nullable().when('publishing_or_master_sync_used', {
    is: 'other',
    then: (schema) => schema.required('Please explain').max(255),
    otherwise: (schema) => schema.nullable()
  })
});

const SelectFieldFormik = formikFieldAdapter(SelectField);
const FormFieldFormik = formikFieldAdapter(FormField);
const RadioFieldFormik = formikFieldAdapter(RadioField);
const DatePickerFieldFormik = formikFieldAdapter(DatePickerField);
const WordsLimitedFieldFormik = formikFieldAdapter(WordsLimitedField);

const UploadSyncStep4 = ({
  initialValues,
  syncOptions,
  syncTerritories,
  savingAll,
  prevStep,
  nextStep,
  bgImg
}) => {

  return (
    <section className="upload-form__sec" style={bgImg}>
      <div className="content">
        <div className="upload-form__top">
          <h2 className="head-shadow">CLEAR SYNC</h2>
          <h3 className="head">Step 4 of 4: Release Info</h3>
        </div>
        <div className="form-step__body">
          <Formik
            initialValues={{
              territory: '',
              release_air_date: '',
              option: '',
              proposed_free: '',
              publishing_or_master_sync_used: '',
              sync_other_used: '',
              ...initialValues
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={(values) => {
              let formData = arrangeData(values);
              formData.release_air_date = convertDateToDb(formData.release_air_date);
              formData.proposed_free = cleanMoney(formData.proposed_free);
              
              if (formData.publishing_or_master_sync_used === "other") {
                delete formData.publishing_or_master_sync_used;
              } else {
                delete formData.sync_other_used;
              }

              nextStep(formData, values);
            }}
          >
            {({ values: currentValues, isSubmitting, setFieldValue, submitCount, errors }) => {
              const textClasses =
                currentValues.publishing_or_master_sync_used === "other"
                  ? "addition-field addition-field_show"
                  : "addition-field addition-field_hide";

              return (
                <Form className="form-layout" noValidate>
                  <div className="form-row">
                    <div className="form-col_2">
                      <Field
                        name="territory"
                        component={SelectFieldFormik}
                        options={syncTerritories}
                        placeholder="Select Territory"
                        label="Territory"
                        required={true}
                        note="State the geographical areas where you would like the rights to use the recording in your project."
                      />
                    </div>
                    <div className="form-col_2">
                      <Field
                        name="release_air_date"
                        component={DatePickerFieldFormik}
                        placeholder="enter MM/DD/YY"
                        label="Release/Air Date"
                        required={true}
                        note="State the date your project will be released or first aired."
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-col_2">
                      <Field
                        name="option"
                        component={SelectFieldFormik}
                        options={syncOptions}
                        placeholder="Options"
                        label="Options"
                        required={true}
                        note="If you would like the option to purchase ADDITIONAL rights in the future."
                      />
                    </div>
                    <div className="form-col_2">
                      <Field
                        name="proposed_free"
                        component={FormFieldFormik}
                        variant="outlined"
                        placeholder="Set Fee"
                        label="Proposed Fee"
                        required={true}
                        note="State the fee you would like to offer in order to obtain the rights you are seeking."
                        input={{
                          name: 'proposed_free',
                          value: normalizeMoney(currentValues.proposed_free),
                          onChange: (e) => {
                            const val = e && e.target ? e.target.value : e;
                            const cleaned = cleanMoney(val);
                            setFieldValue('proposed_free', cleaned);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="upload-form__checks">
                    <h5 className="label-single label-single_required">
                      Type of use
                    </h5>
                    <Field
                      name="publishing_or_master_sync_used"
                      component={RadioFieldFormik}
                      value="master"
                      label="Sound Recording and Composition Used"
                      type="radio"
                    />
                    <Field
                      name="publishing_or_master_sync_used"
                      component={RadioFieldFormik}
                      value="publishing"
                      label="Composition Used"
                      type="radio"
                    />
                    <Field
                      name="publishing_or_master_sync_used"
                      component={RadioFieldFormik}
                      value="other"
                      label="Other"
                      type="radio"
                    />
                    {submitCount > 0 && errors.publishing_or_master_sync_used && (
                      <div className="form-field__note form-field__note_other error">
                        {errors.publishing_or_master_sync_used}
                      </div>
                    )}
                  </div>
                  <div className={textClasses}>
                    <Field
                      name="sync_other_used"
                      component={WordsLimitedFieldFormik}
                      required={true}
                      maxWords={75}
                      placeholder=""
                      label="Please explain"
                    />
                  </div>
                  <div className="submit-row submit-row_txt">
                    <button
                      className="btn btn-second"
                      onClick={() => prevStep(currentValues)}
                      type="button"
                    >
                      Back
                    </button>
                    <button
                      className="btn-red-primary"
                      disabled={isSubmitting || savingAll}
                      type="submit"
                    >
                      Next
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default UploadSyncStep4;
